import { Route, Redirect, withRouter } from 'react-router-dom';
import React, { Suspense, lazy, Fragment, useEffect, useRef, useState } from 'react';
import Loader from 'react-loaders';
import { connect } from 'react-redux';
import { get } from 'lodash';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import Notification from 'components/Notification';

import { checkAuth } from 'actions/Auth';
import { isBeautyCareAdmin } from 'actions/User';
import { getDistributors } from 'utils/isRentalUser';
import { checkTokenExpiration, checkRefreshTokenExpiration } from 'utils/token';
import { isSessionRefreshPending } from 'utils/session';

import AppHeader from '../AppHeader';
import AppSidebar from '../AppSidebar';
import AppFooter from '../AppFooter';

import { ToastContainer } from 'react-toastify';
import { withTranslation } from 'react-i18next';

import Loading from 'components/Loading';
import { t } from 'i18n';

const AuthRedirect = lazy(() => import('pages/AuthRedirect'));
const AuthRedirectAdmin = lazy(() => import('pages/AuthRedirectAdmin'));
const DeleteAccount = lazy(() => import('pages/DeleteAccount'));

const UserList = lazy(() => import('pages/UserList'));
const LicenseDetails = lazy(() => import('pages/LicenseDetails'));
const PowerboxDetails = lazy(() => import('pages/PowerboxDetails'));
const CreatePowerbox = lazy(() => import('pages/CreatePowerbox'));
const LicenseTemplateDetails = lazy(() => import('pages/LicenseTemplateDetails'));
const CreateLicenseTemplate = lazy(() => import('pages/CreateLicenseTemplate'));
const CreateOrganization = lazy(() => import('pages/CreateOrganization'));
const CreateMember = lazy(() => import('pages/CreateMember'));
const LicenseModels = lazy(() => import('pages/LicenseModels'));
const LicenseList = lazy(() => import('pages/LicenseList'));
const MembersList = lazy(() => import('pages/MembersList'));
const MemberDetails = lazy(() => import('pages/MemberDetails'));
const Blacklist = lazy(() => import('pages/Blacklist'));
const OrganizationList = lazy(() => import('pages/OrganizationList'));
const OrganizationDetails = lazy(() => import('pages/OrganizationDetails'));
const PowerboxList = lazy(() => import('pages/PowerboxList'));
const CreateHomeUser = lazy(() => import('pages/CreateHomeUser'));
const CreateStudioUser = lazy(() => import('pages/CreateStudioUser'));
const CreateRentalUser = lazy(() => import('pages/CreateRentalUser'));
const UserDetails = lazy(() => import('pages/UserDetails'));
const LoginPage = lazy(() => import('pages/Login'));
const EMSUsageReports = lazy(() => import('pages/EMSUsageReports'));
const AppLogs = lazy(() => import('pages/AppLogs'));
const RentUserList = lazy(() => import('pages/RentUserList'));

const authPaths = {
  '/login': true,
  '/auth-redirect': true,
  '/auth-redirect-admin': true,
};

const publicPaths = {
  '/delete-account': true,
};

const checkUnauthorizedPath = (path, pathsToCheck) => {
  return Object.keys(pathsToCheck).reduce((acc, el) => {
    if (path.includes(el)) return true;
    return acc;
  }, false);
};

const AuthPageLoader = ({ message = t('common.loading') }) => (
  <div className="loader-container">
    <div className="loader-container-inner">
      <div className="text-center">
        <Loader type="ball-grid-beat" />
      </div>
      <h6 className="mt-3">{message}</h6>
    </div>
  </div>
);

const getAuthLoaderMessage = (pathname) => {
  if (pathname.includes('/auth-redirect-admin')) return t('auth.signingIn');
  if (pathname.includes('/auth-redirect')) return t('auth.checkingSession');
  if (pathname.includes('/login')) return t('auth.redirecting');
  return t('common.loading');
};

// NXT-598: the credential state has to be read from the current storage on
// every render, because Redux can still hold a session that another tab (or
// an expired token) has already invalidated.
const hasUsableAccessToken = () => {
  const token = checkTokenExpiration();
  return !!token && token !== 'expired';
};

const hasUsableRefreshToken = () => {
  const token = checkRefreshTokenExpiration();
  return !!token && token !== 'expired';
};

const AppMain = (props) => {
  const { checkAuth, checkIsBeautyCareAdmin, userRole, authChecked, isAuthenticated, i18n } = props;
  const currentLanguage = String(i18n.resolvedLanguage || i18n.language || 'en')
    .split('-')[0]
    .toLowerCase();
  const useWideSidebar = ['pt', 'ru', 'pl', 'cs', 'el', 'da'].includes(currentLanguage);
  const pathname = props.location.pathname;
  const isAuthPath = checkUnauthorizedPath(pathname, authPaths);
  const isPublicPath = checkUnauthorizedPath(pathname, publicPaths);
  // A session that was verified for another route never authorizes this one;
  // every navigation has to be verified again before its content mounts.
  const [verifiedPath, setVerifiedPath] = useState(null);
  // NXT-598: the credential state is read from storage during the render, so
  // a session that another tab invalidated (or that expired) cannot keep the
  // protected area open through stale Redux state.
  const credentialsUsable = hasUsableAccessToken();
  const refreshPending = !credentialsUsable && hasUsableRefreshToken();
  const sessionVerified = authChecked && isAuthenticated && credentialsUsable && verifiedPath === pathname;
  window.scrollTo(0, 0);
  // NXT-598: verify the session (IQ_TOKEN et al.) on every navigation to a
  // protected or authentication page before any protected data is loaded -
  // and, for the currently rendered protected route, also whenever its
  // credentials turn unusable without a navigation (e.g. the access token
  // expires while the user idles on the page and an unrelated re-render,
  // such as a language change through withTranslation, closes the gate).
  // Without that second start the closed gate would keep waiting for a
  // refresh or redirect that nobody ever begins. The ref remembers the route
  // and the credential state of the last started check, so plain re-renders
  // cannot stack duplicate session checks or repeated login redirects. A
  // refresh already started by another route or the interceptor supplies the
  // Redux result for this route too. When it restores the credentials
  // (a successful deferred refresh), the gate reopens without re-dispatching
  // the session check.
  const sessionCheckRef = useRef(null);
  useEffect(() => {
    if (isPublicPath) {
      sessionCheckRef.current = null;
      return;
    }
    const previous = sessionCheckRef.current;
    const routeChanged = !previous || previous.path !== pathname;
    const credentials = credentialsUsable ? 'usable' : 'unusable';
    if (!routeChanged && previous.credentials === credentials) return;
    if (!routeChanged && credentialsUsable) {
      // The credentials were restored by a verification that was already
      // started for this route; only remember the state so that a later
      // expiry starts one fresh check.
      sessionCheckRef.current = { path: pathname, credentials };
      return;
    }
    sessionCheckRef.current = { path: pathname, credentials };
    // Joining an outstanding refresh also avoids queuing extra loading
    // actions for each navigation while the same verification is pending.
    if (isSessionRefreshPending()) return;
    checkAuth();
  }, [checkAuth, isPublicPath, isAuthPath, pathname, credentialsUsable]);
  useEffect(() => {
    if (isPublicPath || isAuthPath) {
      setVerifiedPath((current) => (current === null ? current : null));
      return;
    }
    // Only a check that agrees with the credentials currently in storage may
    // release this path; otherwise the gate stays closed. credentialsUsable is
    // part of the dependency set so that a deferred token refresh (expired
    // access token stored -> saga stores fresh credentials and dispatches
    // GET_TOKEN.SUCCESS) re-runs this verification and reopens the route the
    // user navigated to, instead of leaving the loader mounted forever.
    const verified = authChecked && isAuthenticated && hasUsableAccessToken();
    setVerifiedPath((current) => (verified ? pathname : current === null ? current : null));
  }, [authChecked, isAuthenticated, credentialsUsable, pathname, isPublicPath, isAuthPath]);
  useEffect(() => {
    if (!userRole || !sessionVerified) return;
    checkIsBeautyCareAdmin();
    if (userRole === 'admin') getDistributors();
  }, [checkIsBeautyCareAdmin, userRole, sessionVerified]);

  if (isPublicPath) {
    return (
      <Suspense fallback={<AuthPageLoader />}>
        <Route exact path="/delete-account" component={DeleteAccount} />
      </Suspense>
    );
  }

  if (isAuthPath) {
    const message = getAuthLoaderMessage(pathname);
    return (
      <Suspense fallback={<AuthPageLoader message={message} />}>
        <AuthPageLoader message={message} />
        <Route path="/login" component={LoginPage} />
        <ToastContainer />
        <Notification />
        <Route path="/auth-redirect" component={AuthRedirect} />
        <Route path="/auth-redirect-admin" component={AuthRedirectAdmin} />
        <Loading loading={props.loading} />
      </Suspense>
    );
  }

  // Authentication gate (NXT-598): protected pages, layout components and
  // their data-loading effects must never render before the session check
  // agrees with the credentials currently in storage. The check itself is
  // started on every navigation and whenever the credentials of the rendered
  // route turn unusable, and it performs the refresh or starts the Keycloak
  // login flow when the session is gone; here the protected area - and with
  // it every API request it would fire - stays unmounted until:
  //   * the check produced a definitive result (authChecked),
  //   * the stored access token is present and unexpired,
  //   * the check confirmed a session for the route being rendered.
  if (!authChecked) {
    return <AuthPageLoader message={t('auth.checkingSession')} />;
  }

  if (!credentialsUsable) {
    // Expired token with a usable refresh token: the session verification
    // started above refreshes the session in place, so keep the protected
    // area unmounted meanwhile. Without any usable credential that
    // verification performs the logout and redirects to the login.
    return <AuthPageLoader message={refreshPending ? t('auth.checkingSession') : t('auth.redirecting')} />;
  }

  if (!isAuthenticated || verifiedPath !== pathname) {
    return <AuthPageLoader message={t('auth.checkingSession')} />;
  }

  if (userRole === 'group_admin' && pathname === '/organizations') {
    return <Redirect to="/admins" />;
  }

  return (
    <Fragment>
      <ReactCSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear={true}
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
      >
        <div className={`d-container admin-container ${useWideSidebar ? 'd-container--wide-sidebar' : ''}`}>
          <div className="d-sidebar-base"></div>
          <AppSidebar />
          <div className="d-content">
            <AppHeader noTabs />
            <div className="app-main__outer">
              <div className="app-main__inner app-layout-main" key={i18n.resolvedLanguage || i18n.language}>
                <div className="">
                  <Suspense
                    fallback={
                      <div className="loader-container">
                        <div className="loader-container-inner">
                          <div className="text-center">
                            <Loader type="ball-grid-beat" />
                          </div>
                          <h6 className="mt-3">{t('common.loading')}</h6>
                        </div>
                      </div>
                    }
                  >
                    <Route exact path="/users" component={UserList} />
                    <Route exact path="/licenses" component={LicenseList} />
                    <Route exact path="/license-details/:id" component={LicenseDetails} />
                    <Route exact path="/admins" component={MembersList} />
                    <Route exact path="/license-types" component={LicenseModels} />
                    <Route exact path="/view-license-type/:id" component={LicenseTemplateDetails} />
                    <Route exact path="/create-license-type" component={CreateLicenseTemplate} />
                    <Route exact path="/create-home-user" component={CreateHomeUser} />
                    <Route exact path="/create-studio-user" component={CreateStudioUser} />
                    {(props.userRole === 'group_admin' || props.userRole === 'admin') && (
                      <Route exact path="/create-hybrid-user" component={CreateRentalUser} />
                    )}
                    <Route exact path="/create-organization" component={CreateOrganization} />
                    <Route
                      exact
                      path="/update-user/:id"
                      render={(routeProps) => <UserDetails {...routeProps} key={routeProps.match.params.id} />}
                    />
                    <Route
                      exact
                      path="/view-user/:id"
                      render={(routeProps) => <UserDetails {...routeProps} key={routeProps.match.params.id} />}
                    />
                    <Route exact path="/powerboxes" component={PowerboxList} />
                    {props.userRole === 'admin' && <Route exact path="/app-logs" component={AppLogs} />}
                    {(props.userRole === 'admin' || props.canCurrentUserCreateLicenses) && (
                      <>
                        <Route exact path="/update-powerbox/:id" component={PowerboxDetails} />
                        <Route exact path="/create-powerbox" component={CreatePowerbox} />
                      </>
                    )}
                    {props.userRole === 'group_admin' && <Route exact path="/hybrid-users" component={RentUserList} />}
                    {(props.userRole === 'admin' || props.userRole === 'group_admin') && (
                      <Route exact path="/ems-usage-reports" component={EMSUsageReports} />
                    )}
                    <Route exact path="/view-powerbox/:id" component={PowerboxDetails} />
                    {props.userRole !== 'chain_manager' && <Route exact path="/blacklists" component={Blacklist} />}
                    <Route exact path="/update-admin/:id" component={MemberDetails} />
                    <Route exact path="/view-admin/:id" component={MemberDetails} />
                    <Route exact path="/create-admin" component={CreateMember} />
                    {props.userRole !== 'group_admin' && (
                      <Route exact path="/organizations" component={OrganizationList} />
                    )}
                    <Route exact path="/update-organization/:id" component={OrganizationDetails} />

                    {props.userRole !== 'group_admin' && (
                      <Route exact path="/" render={() => <Redirect to="/organizations" />} />
                    )}

                    {props.userRole === 'group_admin' && (
                      <Route exact path="/" render={() => <Redirect to="/admins" />} />
                    )}
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReactCSSTransitionGroup>

      <ToastContainer />
      <Notification />
      <Loading loading={props.loading} />
      <AppFooter />
    </Fragment>
  );
};

const mapStateToProps = ({ UserRoles, Loading, Theme, Auth }) => ({
  userRole: UserRoles.userRole,
  loading: Loading.loading,
  darkTheme: Theme.darkTheme,
  authChecked: get(Auth, 'authChecked', false),
  // Expose the session object itself (not just a boolean) so a successful
  // token refresh, which replaces Auth.user, re-renders AppMain and lets the
  // verification effect re-read the freshly stored credentials (NXT-598).
  authUser: get(Auth, 'user'),
  isAuthenticated: !!get(Auth, 'user'),
  canCurrentUserCreateLicenses: get(Auth, 'canCurrentUserCreateLicenses'),
});

const mapDispatchToProps = (dispatch) => ({
  checkAuth: () => dispatch(checkAuth.request()),
  setDarkTheme: (payload) => dispatch({ type: 'SET_THEME', payload }),
  checkIsBeautyCareAdmin: () => dispatch(isBeautyCareAdmin.request()),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(withRouter(AppMain)));
